/**
 * TaskOutputForm — inline output editing for single-respondent Tasks
 * (issue #1680, epic #1674).
 *
 * `TaskTypeRegistry` (`backend/src/utils/taskTypeRegistry.ts`) ships with no
 * entries yet (issue #1678/#1676), so there is no `taskType`-specific output
 * schema to render a tailored form against. This renders `Task.output`
 * (`Record<string, unknown>`) as an editable list of string key/value
 * pairs — the smallest generic editor that can represent any inline output
 * shape a future `taskType` registers, without guessing that shape. Once a
 * consumer sub-issue (#1681/#1682) registers a real `outputSchema`, that
 * `taskType` can render its own tailored form instead of falling through to
 * this one — this component is the fallback for every unregistered type.
 *
 * Submitting sets `Task.output` and progresses `status` to `Complete` (the
 * assignee's work is done, pending owner approval) — allowed for any
 * `Open`/`InProgress` single-respondent Task per
 * `canTransitionTaskStatus` (`backend/src/entities/Task.ts`).
 */

import { useState } from 'react';
import { ForgeButton, ForgeIcon } from '@tylertech/forge-react';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/text-field';
import type { Task } from '../../graphql/types/task';
import { updateTask, ApiError } from '../../lib/apiClient';
import './TaskOutputForm.css';

const ForgeTextField = 'forge-text-field' as React.ElementType;

export interface TaskOutputFormProps {
  task: Task;
  onSubmitted: (updated: Task) => void;
}

interface OutputRow {
  key: string;
  value: string;
}

function outputToRows(output: Record<string, unknown> | null | undefined): OutputRow[] {
  const entries = Object.entries(output ?? {});
  if (entries.length === 0) {
    return [{ key: '', value: '' }];
  }
  return entries.map(([key, value]) => ({ key, value: String(value) }));
}

function rowsToOutput(rows: OutputRow[]): Record<string, unknown> {
  const output: Record<string, unknown> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (key) {
      // key comes from the assignee's own free-text field-name input, never
      // used to access an existing object's prototype/internals — a plain
      // write into a fresh, caller-owned object literal.
      // eslint-disable-next-line security/detect-object-injection
      output[key] = row.value;
    }
  }
  return output;
}

export function TaskOutputForm({ task, onSubmitted }: TaskOutputFormProps): JSX.Element {
  const [rows, setRows] = useState<OutputRow[]>(() => outputToRows(task.output));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRow = (index: number, field: 'key' | 'value', value: string): void => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = (): void => {
    setRows((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeRow = (index: number): void => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const updated = await updateTask(task.entityId, {
        output: rowsToOutput(rows),
        status: 'Complete',
      });
      onSubmitted(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to submit Task output');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="task-output-form" data-testid="task-output-form">
      <p className="forge-typography--body2 task-output-form__hint">
        Record the result of this task below, then submit for approval.
      </p>

      {rows.map((row, index) => (
        <div className="task-output-form__row" key={index}>
          <ForgeTextField className="task-output-form__field">
            <input
              type="text"
              placeholder="Field name"
              aria-label={`Field name ${index + 1}`}
              value={row.key}
              onChange={(e) => updateRow(index, 'key', e.target.value)}
            />
          </ForgeTextField>
          <ForgeTextField className="task-output-form__field">
            <input
              type="text"
              placeholder="Value"
              aria-label={`Field value ${index + 1}`}
              value={row.value}
              onChange={(e) => updateRow(index, 'value', e.target.value)}
            />
          </ForgeTextField>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() => removeRow(index)}
            aria-label={`Remove field ${index + 1}`}
          >
            <ForgeIcon name="remove" aria-hidden="true" />
          </ForgeButton>
        </div>
      ))}

      <ForgeButton type="button" variant="text" onClick={addRow}>
        <ForgeIcon name="add_circle" slot="start" aria-hidden="true" />
        Add field
      </ForgeButton>

      {error && (
        <p className="forge-typography--body2 task-output-form__error" role="alert">
          {error}
        </p>
      )}

      <div className="task-output-form__actions">
        <ForgeButton
          type="button"
          variant="raised"
          onClick={() => void handleSubmit()}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </ForgeButton>
      </div>
    </div>
  );
}
